import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutDashboard, FileText, FolderKanban, BarChart3, Users, Settings, LogOut, ChevronRight, Plus } from "lucide-react";
import { ADMIN_STATS, ARTICLES, PROJECTS, CADENCE } from "../data/mock";
import WordEditor from "../components/admin/WordEditor";

const MENU = [
  { id: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { id: "articles", label: "Articles", Icon: FileText },
  { id: "projects", label: "Projects", Icon: FolderKanban },
  { id: "analytics", label: "Analytics", Icon: BarChart3 },
  { id: "visitors", label: "Visitors", Icon: Users },
  { id: "settings", label: "Settings", Icon: Settings },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("dashboard");
  const [editor, setEditor] = useState(null); // article in editor
  const authed = typeof window !== "undefined" && sessionStorage.getItem("fmi-admin") === "1";
  if (!authed) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* sidebar */}
      <aside className="w-64 border-r border-border/60 bg-card/40 backdrop-blur-md flex flex-col">
        <div className="px-5 py-5 border-b border-border/60 flex items-center justify-between">
          <span className="font-mono text-[12px] tracking-[0.2em]">fmi.dev / admin</span>
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
        </div>
        <nav className="p-3 space-y-1 flex-1">
          {MENU.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => { setTab(id); setEditor(null); }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-colors ${tab === id ? "bg-primary/15 text-primary" : "text-foreground/80 hover:bg-secondary/60"}`}>
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border/60">
          <Link to="/" onClick={() => sessionStorage.removeItem("fmi-admin")} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] text-muted-foreground hover:bg-secondary/60">
            <LogOut className="h-4 w-4" /> Sign out
          </Link>
        </div>
      </aside>

      {/* main */}
      <main className="flex-1 overflow-y-auto">
        <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-md border-b border-border/60 px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
            <span>Admin</span> <ChevronRight className="h-3 w-3" /> <span className="text-foreground">{tab}</span>
            {editor && (<><ChevronRight className="h-3 w-3" /> <span className="text-primary">{editor.title}</span></>)}
          </div>
          {tab === "articles" && !editor && (
            <button onClick={() => setEditor({ title: "Untitled draft", body: "Start writing…" })} className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-primary text-primary-foreground font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-primary/90">
              <Plus className="h-3.5 w-3.5" /> New article
            </button>
          )}
        </header>

        <div className="p-8">
          <AnimatePresence mode="wait">
            {editor ? (
              <motion.div key="editor" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <WordEditor initial={editor} onClose={() => setEditor(null)} />
              </motion.div>
            ) : (
              <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
                {tab === "dashboard" && <DashboardView />}
                {tab === "articles" && <ArticlesView onEdit={(a) => setEditor(a)} />}
                {tab === "projects" && <ProjectsView />}
                {tab === "analytics" && <AnalyticsView />}
                {tab === "visitors" && <VisitorsView />}
                {tab === "settings" && <SettingsView />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

function DashboardView() {
  const max = Math.max(...CADENCE.map((c) => c.commits));
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {ADMIN_STATS.map((s) => (
          <div key={s.k} className="rounded-xl border border-border/60 bg-card/40 p-5">
            <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground">{s.k}</p>
            <div className="mt-3 flex items-end justify-between">
              <span className="text-3xl font-medium tracking-display">{s.v}</span>
              <span className="text-[12px] text-emerald-300 font-mono">{s.delta}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-border/60 bg-card/40 p-6">
        <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground mb-6">Cadence</p>
        <div className="flex items-end gap-4 h-48 px-2">
          {CADENCE.map((c, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center justify-end gap-2 h-full">
              <motion.div initial={{ height: 0 }} animate={{ height: `${(c.commits / max) * 90}%` }} transition={{ duration: 0.8, delay: idx * 0.06, ease: [0.2,0.7,0.2,1] }} className="w-10 rounded-t bg-gradient-to-t from-violet-600 to-violet-300" />
              <span className="text-[10px] text-muted-foreground font-mono">{c.m}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ArticlesView({ onEdit }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/40 overflow-hidden">
      <table className="w-full text-[13px]">
        <thead className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground bg-secondary/40">
          <tr><th className="text-left px-5 py-3">Title</th><th className="text-left px-5 py-3">Category</th><th className="text-left px-5 py-3">Date</th><th className="text-right px-5 py-3">Actions</th></tr>
        </thead>
        <tbody>
          {ARTICLES.map((a) => (
            <tr key={a.slug} className="border-t border-border/60 hover:bg-secondary/30">
              <td className="px-5 py-3">{a.title}</td>
              <td className="px-5 py-3 text-muted-foreground">{a.category}</td>
              <td className="px-5 py-3 text-muted-foreground font-mono">{a.date}</td>
              <td className="px-5 py-3 text-right"><button onClick={() => onEdit(a)} className="font-mono text-[10px] tracking-[0.2em] uppercase text-primary hover:underline">Edit</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectsView() {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {PROJECTS.map((p) => (
        <div key={p.id} className="rounded-xl border border-border/60 bg-card/40 p-5">
          <div className="flex items-center justify-between"><h4 className="text-xl font-medium">{p.name}</h4><span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{p.type}</span></div>
          <p className="text-sm text-foreground/75 mt-2">{p.desc}</p>
          <div className="mt-3 flex flex-wrap gap-2">{p.stack.map((t) => <span key={t} className="text-[10px] font-mono px-2 py-1 border border-border/60 rounded-full">{t}</span>)}</div>
        </div>
      ))}
    </div>
  );
}

function AnalyticsView() {
  return <div className="rounded-xl border border-border/60 bg-card/40 p-10 text-center text-muted-foreground">{/* Placeholder */} Lorem ipsum analytics dashboard — wire to backend later.</div>;
}
function VisitorsView() {
  return <div className="rounded-xl border border-border/60 bg-card/40 p-10 text-center text-muted-foreground">{/* Placeholder */} Visitor stream — mock.</div>;
}
function SettingsView() {
  return <div className="rounded-xl border border-border/60 bg-card/40 p-10 text-center text-muted-foreground">{/* Placeholder */} Settings panel — mock.</div>;
}
